/**
 * @file ClientList.js
 * @description This page displays a list of clients with their respective details such as name, code, etc...
 */
import '../page-list/PageList.css';
import styles from './ClientList.module.scss';
import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContentType } from '../helpers/getContentType';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import PaginationComponent from '../helpers/PaginationComponent';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import { NavLink } from 'react-router-dom';
import {
    fetchClients,
    updateClient,
    favoriteClient,
    getClient,
    deleteClient,
} from '../../services/clientService';
import CountsPopup from './components/CountsPopup';
import { useAuthContext } from '../../store/AuthContext';
import { useTableSort, SortDirections } from '../../hooks/useTableSort';
import { handleNavigateToClient, getTierIcon } from '../../utils/utils';
import { SecurityAttributes, TenantStatus } from '../../utils/data/enums';
import { ReactComponent as ViewIcon } from '../../utils/icons/Icon_View.svg';
import './ClientList.scss';
import { LogEventType, tenantTierLabels } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';

const ITEMS_PER_PAGE = 50;

const ClientList = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const [rowsClicked, setRowsClicked] = useState([]);
    const { sortConfig, resetSortConfig, renderSortArrow } = useTableSort(
        'isFavorite',
        SortDirections.DESCENDING,
    );
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalVisible, setModalVisible] = useState(false);
    const [countData, setCountData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const { hasSecurityAttribute } = useAuthContext();

    const selectAllRef = useRef(null);

    useEffect(() => {
        const searchFiltered = clients.filter(
            client =>
                client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                client.code?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setBaseFilteredData(searchFiltered);
    }, [searchTerm, clients]);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        resetSortConfig();
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
        setCurrentPage(1);
    };

    // Compute available options considering active filters for current modal
    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column]; // Remove the current column's filter

        // Use these modified filters to filter data
        const filteredData = clients.filter(client =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(client[key]),
            ),
        );

        // Now get all unique values for the current column from this filtered dataset
        return Array.from(new Set(filteredData.map(item => item[column]))).sort(
            (a, b) => {
                if (column === 'tier') {
                    const aId =
                        filteredData.find(item => item.tier === a)
                            ?.tenantTierId ?? 0;
                    const bId =
                        filteredData.find(item => item.tier === b)
                            ?.tenantTierId ?? 0;
                    return aId - bId;
                }
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            },
        );
    };

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            const fetchedClients = await fetchClients([]);
            setClients(fetchedClients);
            setBaseFilteredData(fetchedClients);
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to fetch clients', error);
            Toast.error(getText('msg_7318fe9ba00c'));
        } finally {
            setIsLoading(false);
        }
    };

    const getTypeString = type => {
        switch (type) {
            case 0:
                return 'Inactive';
            case 1:
                return 'Live';
            case 2:
                return 'In Progress';
            case 3:
                return 'Demo';
            case 4:
                return 'Test';
            case 5:
                return 'Template';
            case 6:
                return 'Implementation';
            case 7:
                return 'Scheduled for Deletion';
            case 8:
                return 'Contracting';
            case 9:
                return 'Pre Launch';
            default:
                return 'Undefined';
        }
    };

    const getTypeClass = type => {
        switch (type) {
            case 0:
            case 'Inactive':
                return 'ing-badge-inactive';
            case 1:
            case 'Live':
                return 'ing-badge-active';
            case 2:
            case 'In Progress':
                return 'ing-badge-in-progress';
            case 3:
            case 'Demo':
                return 'ing-badge-completed';
            case 4:
            case 'Test':
                return 'ing-badge-inactive';
            case 5:
            case 'Template':
                return 'ing-badge-inv-sent';
            case 6:
            case 'Implementation':
                return 'ing-badge-in-progress';
            case 7:
            case 'Scheduled for Deletion':
                return 'ing-badge-error';
            case 8:
            case 'Contracting':
                return 'ing-badge-complete-with-errors';
            case 9:
            case 'Pre Launch':
                return 'ing-badge-template';
            default:
                return 'ing-badge-inactive';
        }
    };

    const sortedClients = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue;
            let bValue;
            if (sortConfig.key === 'type') {
                aValue = getContentType(a[sortConfig.key]);
                bValue = getContentType(b[sortConfig.key]);
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }

            // Handle null or undefined values
            if (!aValue) aValue = '';
            if (!bValue) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const paginatedClients = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return sortedClients.slice(startIndex, endIndex);
    }, [currentPage, sortedClients]);

    useEffect(() => {
        setTotalPages(Math.ceil(sortedClients.length / ITEMS_PER_PAGE));
        setCurrentPage(1);
    }, [sortedClients]);

    const selectedRowsOnPage = useMemo(() => {
        if (!paginatedClients.length) {
            return 0;
        }

        return paginatedClients.filter(client =>
            rowsClicked.includes(client.id),
        ).length;
    }, [paginatedClients, rowsClicked]);

    const handlePageChange = newPage => {
        setCurrentPage(newPage);
    };

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                selectedRowsOnPage > 0 &&
                selectedRowsOnPage < paginatedClients.length;
        }
    }, [selectedRowsOnPage, paginatedClients]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const handlerRowFavorited = id => {
        const updatedClients = clients.map(client => {
            if (client.id === id) {
                return { ...client, isFavorite: !client.isFavorite };
            }
            return client;
        });

        setClients(updatedClients);
        favoriteClient(id).catch(error => {
            console.error('Failed to update favorite status', error);
            Toast.error(getText('msg_45ab5a3e583e'));
            setClients(clients);
        });
    };

    const handlerRowClicked = id => {
        setRowsClicked(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id],
        );
    };

    const handleSelectAll = event => {
        const currentPageClientIds = paginatedClients.map(client => client.id);

        if (event.target.checked) {
            setRowsClicked(prev => [
                ...new Set([...prev, ...currentPageClientIds]),
            ]);
        } else {
            setRowsClicked(prev =>
                prev.filter(id => !currentPageClientIds.includes(id)),
            );
        }
    };

    const onToggle = async id => {
        try {
            const tenant = await getClient(id);

            if (!tenant) throw new Error('Client not found');

            const updatedTenant = { ...tenant, isActive: !tenant.isActive };

            await updateClient(updatedTenant);

            Toast.success(
                `Client ${tenant.isActive ? 'disabled' : 'enabled'} successfully`,
            );
            loadClients();
        } catch (error) {
            console.error('Failed to toggle client enabled status', error);
            Toast.error(getText('msg_2ffa6e8e2311'));
        }
    };

    const deleteClients = () => {
        rowsClicked.forEach(row => {
            clients.filter(client => {
                if (client.id === row) {
                    if (
                        client.tenantStatusId !== TenantStatus.Live &&
                        client.tenantStatusId !==
                            TenantStatus.ScheduledForDeletion
                    ) {
                        deleteClient(client.id).then(() => {
                            Toast.success(
                                "Client '" +
                                    client.name +
                                    "' has been scheduled for deletion",
                            );
                            deselectRows(rowsClicked);
                            loadClients();
                        });
                    } else {
                        Toast.error(
                            "You don't have the necessary permissions to delete '" +
                                client.name +
                                "'.",
                        );
                    }
                }
                return null;
            });
        });
    };

    const deselectRows = rowsClicked => {
        rowsClicked.forEach(row => {
            const checkbox = document.getElementById(row);
            if (checkbox) {
                checkbox.checked = false;
            }
        });
        setRowsClicked([]);
    };

    const rowAction = action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete':
                    showModal({
                        type: ModalTypes.DELETE_CONFIRMATION,
                        title: getText('msg_e9cdb1fbfe6b'),
                        text: getText('msg_1b24ec29ffde'),
                        onYes: () => deleteClients(),
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_f6fdbe48dc54'),
                    });
                    logEvent(
                        LogEventType.DeleteClicked,
                        `Client delete clicked`,
                        null,
                    );
                    break;
                default:
                    break;
            }
        } else {
            Toast.error(getText('msg_9bcf67dc54d5'));
        }
    };

    const handleCountClick = (event, counts) => {
        const rect = event.target.getBoundingClientRect();
        setModalPosition({ x: rect.left, y: rect.top });
        setModalVisible(true);
        setCountData(counts);
    };

    // Use shared native navigation helper from utils instead of a local implementation

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="client-list-container page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    {/* Screen Header with Title and Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        {/* Screen Title */}
                        <div className="float-start d-flex align-items-center">
                            <p
                                className={`page-title card-title ms-4 me-5 ${styles.clientListTitle}`}
                            >
                                {getText('msg_2a8a1e3391aa')}
                            </p>
                            <div className={styles.searchWrapper}>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={getText('msg_c135093ed832')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                <span className={styles.searchIcon}>
                                    <i className="bi bi-search"></i>
                                </span>
                            </div>
                        </div>
                        {/* Screen Actions */}
                        <div className="float-end d-flex align-items-center">
                            {hasSecurityAttribute([
                                SecurityAttributes.ClientDelete,
                            ]) && (
                                <>
                                    <span
                                        type="button"
                                        onClick={() => rowAction('delete')}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-trash3 me-2" />
                                        {getText('msg_f6fdbe48dc54')}
                                    </span>
                                </>
                            )}

                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>

                            {hasSecurityAttribute([
                                SecurityAttributes.ClientFullAccess,
                            ]) && (
                                <span
                                    onClick={() => navigate(ROUTES.CLIENTS_ADD)}
                                    type="button"
                                    className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        {/* Template List Table */}
                        <table
                            className={`table table-hover align-middle ${styles.clientTable}`}
                        >
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th
                                        className={`text-center ${styles.iconColumn}`}
                                    >
                                        <i
                                            className="bi bi-star-fill"
                                            style={{ color: '#FFA800' }}
                                        ></i>
                                        {renderSortArrow('isFavorite')}
                                    </th>
                                    <th
                                        className={`text-center ${styles.selectionColumn}`}
                                    >
                                        {hasSecurityAttribute([
                                            SecurityAttributes.ClientDelete,
                                        ]) && (
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                ref={selectAllRef}
                                                checked={
                                                    selectedRowsOnPage ===
                                                        paginatedClients.length &&
                                                    paginatedClients.length > 0
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        {renderSortArrow('name')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('name');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['name']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_6346915c34e2')}
                                        </span>
                                        {renderSortArrow('code')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('code');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['code']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'code' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th className="text-end">
                                        <span className="table-column-header-text">
                                            {getText('msg_35ecb6036dd4')}
                                        </span>
                                        {renderSortArrow('totalCount')}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_5c47e26c6ad2')}
                                        </span>
                                        {renderSortArrow('agency')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('agency');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['agency']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'agency' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_a259ac911046')}
                                        </span>
                                        {renderSortArrow('agencyIsReseller')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('agencyIsReseller');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['agencyIsReseller']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'agencyIsReseller' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                getString={value =>
                                                    value ? 'Yes' : 'No'
                                                }
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_5bd44ebe63cd')}
                                        </span>
                                        {renderSortArrow('tier')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('tier');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['tier']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'tier' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                displayTierIcon={true}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_3deb74565196')}
                                        </span>
                                        {renderSortArrow('status')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('status');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['status']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'status' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                getString={getTypeString}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_b37456c4530b')}
                                        </span>
                                        {renderSortArrow('contact')}
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('contact');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['contact']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'contact' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        className={`text-center ${styles.enableColumn}`}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_20063ad90532')}
                                        </span>
                                        {renderSortArrow('isActive')}
                                    </th>
                                    <th
                                        className={`text-center ${styles.detailsColumn}`}
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_dc3decbb9384')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedClients.map((client, index) => (
                                    <tr
                                        key={client.id}
                                        style={{
                                            '--bs-table-bg':
                                                index % 2 === 1
                                                    ? '#f8f9fa'
                                                    : '',
                                        }}
                                        className={`${rowsClicked.includes(client.id) ? 'clicked-class' : ''}`}
                                    >
                                        <td
                                            className={`text-center ${styles.iconColumn}`}
                                        >
                                            {client.isFavorite ? (
                                                <i
                                                    className="bi bi-star-fill"
                                                    style={{
                                                        color: '#FFA800',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() =>
                                                        handlerRowFavorited(
                                                            client.id,
                                                        )
                                                    }
                                                ></i>
                                            ) : (
                                                <i
                                                    className="bi bi-star"
                                                    style={{
                                                        color: 'lightgray',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() =>
                                                        handlerRowFavorited(
                                                            client.id,
                                                        )
                                                    }
                                                ></i>
                                            )}
                                        </td>
                                        <td
                                            className={`text-center ${styles.selectionColumn}`}
                                        >
                                            {hasSecurityAttribute([
                                                SecurityAttributes.ClientDelete,
                                            ]) &&
                                                client.tenantStatusId !==
                                                    TenantStatus.Live &&
                                                client.tenantStatusId !==
                                                    TenantStatus.ScheduledForDeletion && (
                                                    <input
                                                        id={client.id}
                                                        checked={rowsClicked.includes(
                                                            client.id,
                                                        )}
                                                        onChange={() =>
                                                            handlerRowClicked(
                                                                client.id,
                                                            )
                                                        }
                                                        className="form-check-input"
                                                        type="checkbox"
                                                    />
                                                )}
                                        </td>
                                        <td
                                            className={`titleCell ${styles.nameCell}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                handleNavigateToClient(
                                                    client.id,
                                                    client.code,
                                                    client.name,
                                                    false,
                                                    true,
                                                )
                                            }
                                            onKeyDown={e => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    handleNavigateToClient(
                                                        client.id,
                                                        client.code,
                                                        client.name,
                                                        false,
                                                        true,
                                                    );
                                                }
                                            }}
                                        >
                                            {client.name}
                                        </td>
                                        <td className="subtitleCell">
                                            {client.code}
                                        </td>
                                        <td
                                            className="subtitleCell text-end"
                                            onClick={event =>
                                                handleCountClick(
                                                    event,
                                                    client.countDetails,
                                                )
                                            }
                                        >
                                            <span className="client-list-counts-value">
                                                {client.totalCount}
                                            </span>
                                        </td>
                                        <td className="subtitleCell">
                                            {client.agency}
                                        </td>
                                        <td className="text-center subtitleCell">
                                            {client.agencyIsReseller
                                                ? 'Y'
                                                : '-'}
                                        </td>
                                        <td className="item-page-changed-by">
                                            <div className="tier-icon-wrapper">
                                                <div className="hover-tag">
                                                    {
                                                        tenantTierLabels[
                                                            client.tenantTierId
                                                        ]
                                                    }
                                                </div>
                                                {getTierIcon(
                                                    client.tenantTierId,
                                                )}
                                            </div>
                                        </td>
                                        <td className="item-page-changed-by">
                                            <button
                                                className={getTypeClass(
                                                    client.status,
                                                )}
                                            >
                                                {client.status}
                                            </button>
                                        </td>
                                        <td className="subtitleCell">
                                            {client.contact}
                                        </td>
                                        <td
                                            className={`text-center ${styles.enableColumn}`}
                                        >
                                            <div className={`form-switch`}>
                                                <input
                                                    className="form-check-input custom-switch-input"
                                                    type="checkbox"
                                                    checked={client.isActive}
                                                    onChange={() =>
                                                        onToggle(client.id)
                                                    }
                                                    disabled={
                                                        !hasSecurityAttribute([
                                                            SecurityAttributes.ClientFullAccess,
                                                        ])
                                                    }
                                                />
                                            </div>
                                        </td>
                                        <td
                                            className={`text-center ${styles.detailsColumn}`}
                                        >
                                            <NavLink
                                                to={ROUTE_BUILDERS.clientsView(
                                                    client.id,
                                                )}
                                            >
                                                <ViewIcon />
                                            </NavLink>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {sortedClients.length <= 0 ? 'No clients found' : ''}
                    </p>
                    {totalPages > 0 && (
                        <div className="mt-auto">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {modalVisible && (
                <CountsPopup
                    x={modalPosition.x}
                    y={modalPosition.y}
                    onClose={() => setModalVisible(false)}
                    counts={countData}
                />
            )}
        </div>
    );
};
export default ClientList;
